module Meevo.Plugins.AlertsManager {
    import IAlertCategory = Meevo.Model.IAlertCategory;
    import IAlertSetting = Meevo.Model.IAlertSetting;
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;
    import IAlert = Meevo.Model.IAlert;
    import IAlertUserListItem = Meevo.Model.IAlertUserListItem;
    import IAlertCatListItem = Meevo.Model.IAlertCatListItem;
    import NotificationMethodEnum = Meevo.Model.NotificationMethodEnum;
    import VerbEnum = Meevo.Model.VerbEnum;
    import NounEnum = Meevo.Model.NounEnum;
    import TrackingItem = Meevo.Model.TrackingItem;
    import IAlertListItem = Meevo.Model.IAlertListItem;
    import EnumTypeEnum = Meevo.Model.EnumTypeEnum;

    export enum ViewEnum {
        List,
        AddNew
    }

    export enum DisplayViewsEnum {
        Employee,
        Alert
    }

    export interface IAlertCriteria {
        objectStateEnum?: ObjectStateEnum;
        cat_SId?: number;
        user_TId?: Guid;
    }

    export interface IAlertsManagerService {
        alertUserList: Array<IAlertUserListItem>;
        alertCatList: Array<IAlertCatListItem>;
        view: ViewEnum;
        alert: IAlert;
        triggeredByLabels: Model.EnumDto[];

        getList: (view: DisplayViewsEnum, criteria?: IAlertCriteria) => ng.IPromise<IAlertUserListItem[] | IAlertCatListItem[]>;
        getAlertUserList: (criteria?: IAlertCriteria) => ng.IPromise<IAlertUserListItem[]>;
        getAlertCatList: (criteria?: IAlertCriteria) => ng.IPromise<IAlertCatListItem[]>;
        getAlertCoun: (criteria: IAlertCriteria) => ng.IPromise<number>;
        getAlertList: (criteria: IAlertCriteria) => ng.IPromise<IAlert[]>;
        getAlert: (id: string) => ng.IPromise<IAlert>;
        deleteAlert: (alert: IAlertListItem) => ng.IPromise<any>;
        addNew: (alert: IAlert) => ng.IPromise<any>;
        update: (alert: IAlert) => ng.IPromise<any>;
        getAlertCategoryList: () => ng.IPromise<IAlertCategory[]>;
        getAlertSettingList: (catId?: number) => ng.IPromise<IAlertSetting[]>;
        usageTrackingLog: (nounEnum: NounEnum, verbEnum: VerbEnum) => void;
        getSecurityManagerKeys: (id: number) => ng.IPromise<any>;
        getSecurityManagerKeysForSecuredFeature: (id: number) => ng.IPromise<any>;
        getServicesByIds: (ids: string[]) => ng.IPromise<any>;
        getTriggeredByLabels(): ng.IPromise<Model.EnumDto[]>;
        getFeatureList(): ng.IPromise<{ displayName: string; id: string; }[]>;
    }

    export class AlertsManagerService implements IAlertsManagerService {
        private _alertUserList: Array<IAlertUserListItem> = [];
        private _alertCatList: Array<IAlertCatListItem> = [];
        private _view: ViewEnum = ViewEnum.List;
        private _alert: IAlert;
        private _triggeredByLabels: Model.EnumDto[] = [];

        static $inject = [
            'meevo.components.common.DataService',
            'meevo.AppSettings',
            'meevo.components.tracking.TrackingService',
        ];

        constructor(
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected trackingService: Meevo.Components.Tracking.ITrackingService
        ) {
        }

        /**
         * Get list of Alert User(IAlertUserListItem) or Alert Category(IAlertCatListItem)
         * @param {DisplayViewsEnum} view
         * @param {IAlertCriteria} criteria
         */
        getList(view: DisplayViewsEnum, criteria?: IAlertCriteria): ng.IPromise<IAlertUserListItem[] | IAlertCatListItem[]> {
            if (view === DisplayViewsEnum.Employee) {
                return this.getAlertUserList(criteria);
            }

            return this.getAlertCatList(criteria);
        }

        /**
         * Get list of Employees who contain alerts
         */
        getAlertUserList(criteria?: IAlertCriteria): ng.IPromise<IAlertUserListItem[]> {
            return this.dataService.getListPaged('alertUser', { criteria })
                .then((data: Array<IAlertUserListItem>) => this.alertUserList = data);
        }

        /**
         * Get list of Categories which contain alerts
         */
        getAlertCatList(criteria?: IAlertCriteria): ng.IPromise<IAlertCatListItem[]> {
            return this.dataService.getListPaged('alertCat', { criteria })
                .then((data: Array<IAlertCatListItem>) => this.alertCatList = data);
        }

        /**
         * Get count of alerts
         * @param criteria
         */
        getAlertCoun(criteria: IAlertCriteria): ng.IPromise<number> {
            return this.dataService.getCount(this.appSettings.API_ENDPOINTS.alertsManager, { criteria });
        }

        /**
         * Get list of alerts
         * @param criteria
         */
        getAlertList(criteria: IAlertCriteria): ng.IPromise<IAlert[]> {
            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.alertsManager, { criteria })
                .then((alertList: Array<IAlert>) => {
                    alertList.forEach(alert => {
                        alert.oncePerDay = (alert.notificationMethod & NotificationMethodEnum.OnceDay) === NotificationMethodEnum.OnceDay;
                        alert.sms = (alert.notificationMethod & NotificationMethodEnum.Sms) === NotificationMethodEnum.Sms;
                        alert.email = (alert.notificationMethod & NotificationMethodEnum.Email) === NotificationMethodEnum.Email;
                    });

                    return alertList;
                });
        }

        /**
         * Get Alert by id
         * @param {string} id
         */
        getAlert(id: string): ng.IPromise<IAlert> {
            return this.dataService.customMethodData(`api/${this.appSettings.API_ENDPOINTS.alertsManager}/${id}`, 'GET', null)
                .then(response => response.data);
        }

        /**
         * Delete Alert
         * @param {IAlertListItem} alert
         */
        deleteAlert(alert: IAlertListItem): ng.IPromise<any> {
            return this.dataService.deleteWithJsonBody('AlertToUser', {
                list: [{
                    alertId: alert.id,
                    userId: alert.user_TId
                }]
            })
        }

        /**
         * Add new Alert
         * @param {IAlert} alert
         */
        addNew(alert: IAlert): ng.IPromise<any> {
            return this.dataService.add(this.appSettings.API_ENDPOINTS.alertsManager, alert);
        }


        /**
         * Update Alert
         * @param {IAlert} alert
         */
        update(alert: IAlert): ng.IPromise<any> {
            return this.dataService.update(this.appSettings.API_ENDPOINTS.alertsManager, alert);
        }

        /**
         * Get list of Alerts Categories
         */
        getAlertCategoryList(): ng.IPromise<IAlertCategory[]> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.alertCategory}/List`, 'POST', {
                objectStateEnum: ObjectStateEnum.Active
            }).then(response => response.data);
        }

        /**
         * Get list of Alerts
         */
        getAlertSettingList(catId?: number): ng.IPromise<IAlertSetting[]> {
            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.alertSetting, {
                criteria: {
                    objectStateEnum: ObjectStateEnum.Active,
                    cat_SId: catId
                }
            });
        }

        /**
         * Track user activity
         * @param {NounEnum} nounEnum
         * @param {VerbEnum} verbEnum
         */
        usageTrackingLog(nounEnum: NounEnum, verbEnum: VerbEnum): void {
            let trackingItem = new TrackingItem(nounEnum, verbEnum, null);

            this.trackingService.saveTrackingInfo(trackingItem);
        }

        /**
         * Get security keys by feature id
         * @param {number} id
         */
        getSecurityManagerKeys(id: number): ng.IPromise<any> {
            return this.dataService.customMethodData('api/SecurityManager', 'POST', {
                allModules: false,
                allRoles: false,
                isForCOUseOnly: false,
                moduleKeys: [id],
                excludeNonAlertSecurityKeys: true
            }).then(response => response.data);
        }

        /**
         * Get security keys by feature id
         */
        getSecurityManagerKeysForSecuredFeature(id: number): ng.IPromise<any> {
            return this.dataService.customMethodData('api/Alert/CheckAccessKeys', 'POST', {
                allModules: false,
                allRoles: false,
                isForCOUseOnly: false,
                moduleKeys: [id],
                excludeNonAlertSecurityKeys: true
            }).then(response => response.data);
        }

        /**
         * Get list of services by ids
         * @param {string[]} ids
         */
        getServicesByIds(ids: string[]): ng.IPromise<any> {
            return this.dataService
                .customMethodData('api/Service/ByIds/List', 'POST', { ServiceIds: ids })
                .then(response => response.data);
        }

        getTriggeredByLabels(): ng.IPromise<Model.EnumDto[]> {
            return this.dataService.getEnumList(EnumTypeEnum.AlertsTriggeredByEnum)
                .then((res) => {
                    this._triggeredByLabels = res;

                    return res;
                });

        }

        getFeatureList(): ng.IPromise<{ displayName: string; id: string; }[]> {
            return this.dataService
                .getListPaged('securitymanagermoduleselection', {
                    'pageNumber': 0,
                    'itemsPerPage': 20,
                    'criteria': { 'filter': '%' }
                });
        }

        getSecurityManagerModuleSelectionList(criteria): ng.IPromise<{
            level: number;
            sequence: number;
            id: string;
            displayName: string;
        }[]> {
            return this.dataService.getListPaged('securitymanagermoduleselection', {
                criteria,
                itemsPerPage: 1000,
                pageNumber: 0
            })
        }

        get alertUserList(): Array<IAlertUserListItem> {
            return this._alertUserList;
        }

        set alertUserList(list: Array<IAlertUserListItem>) {
            this._alertUserList = list;
        }

        get alertCatList(): Array<IAlertCatListItem> {
            return this._alertCatList;
        }

        set alertCatList(list: Array<IAlertCatListItem>) {
            this._alertCatList = list;
        }

        get view(): ViewEnum {
            return this._view;
        }

        set view(view: ViewEnum) {
            this._view = view;
        }

        get alert(): IAlert {
            return this._alert;
        }

        set alert(alert: IAlert) {
            this._alert = alert;
        }

        get triggeredByLabels(): Model.EnumDto[] {
            return this._triggeredByLabels;
        }
    }

    angular.module('meevo.plugins.alertsManager')
        .service('meevo.plugins.alertsManager.AlertsManagerService', AlertsManagerService);
}
